import { useMemo } from 'react';
import styled from 'styled-components';

import { useVideoCreatorStore } from '../../stores-v2';
import {
  GenericPresetTemplates,
  KaraokePresetTemplates,
} from '../../types.ts/general';
import PresetPreview from './PresetPreview';

import type { AspectRatio } from '../../types.ts/video';
import type { Preset } from '../textProcessor/TextBrandingService';

export default function GlobalKaraokePresetSelect({
  produceKaraokeText,
}: {
  produceKaraokeText: () => Promise<void>;
}) {
  const videoCreator = useVideoCreatorStore();
  const { globalKaraokePresets } = videoCreator.textBrandingService;
  const aspectRatio = videoCreator.getCurrentAspectRatio();

  const presets = useMemo(
    preparePresetsToDisplay(globalKaraokePresets, aspectRatio),
    [globalKaraokePresets, aspectRatio],
  );

  return (
    <Section>
      <SectionText>Arbor Templates</SectionText>
      <PresetsContainer>
        {presets.map(t => (
          <PresetPreview
            key={`${t.id}-${t.aspectRatio}`}
            preset={t}
            produceKaraokeText={produceKaraokeText}
          />
        ))}
      </PresetsContainer>
    </Section>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
`;

const SectionText = styled.div`
  color: #949494;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 159%; /* 15.9px */
  margin-top: 10px;
`;

const PresetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-self: stretch;
`;

// Presets we want to surface first (explicit priority order)
const KNOWN_PRESET_ORDER: string[] = [
  GenericPresetTemplates.default,
  KaraokePresetTemplates.classic,
  KaraokePresetTemplates.typewriter,
  KaraokePresetTemplates.edge,
];
const orderMap = new Map(KNOWN_PRESET_ORDER.map((id, idx) => [id, idx]));

function preparePresetsToDisplay(
  globalKaraokePresets: Preset[],
  aspectRatio: AspectRatio,
): () => Preset[] {
  return () => {
    return globalKaraokePresets
      .filter(
        p =>
          p.aspectRatio === aspectRatio &&
          p.id !== GenericPresetTemplates.custom,
      )
      .sort((a, b) => {
        const aVal = orderMap.get(a.id);
        const bVal = orderMap.get(b.id);
        const aIdx = typeof aVal === 'number' ? aVal : Infinity;
        const bIdx = typeof bVal === 'number' ? bVal : Infinity;
        if (aIdx !== bIdx) return aIdx - bIdx;
        const aT =
          typeof a.title === 'string' ? a.title : String(a.title ?? '');
        const bT =
          typeof b.title === 'string' ? b.title : String(b.title ?? '');
        return aT.localeCompare(bT);
      });
  };
}
